import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { HelmetProvider } from 'react-helmet-async'
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from './contexts/AuthContext'

import App from './App.tsx'
import './styles/index.css'
import 'leaflet/dist/leaflet.css'

// React Query Client Configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    },
    mutations: {
      retry: 1,
    },
  },
})

// Hot Toast Configuration
const toasterOptions = {
  duration: 4000,
  position: 'top-right' as const,
  style: {
    background: '#ffffff',
    color: '#374151',
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    fontFamily: 'Prompt, Inter, sans-serif',
  },
  success: {
    iconTheme: {
      primary: '#10b981',
      secondary: '#ffffff',
    },
    style: {
      border: '1px solid #d1fae5',
    },
  },
  error: {
    iconTheme: {
      primary: '#ef4444',
      secondary: '#ffffff',
    },
    style: {
      border: '1px solid #fecaca',
    },
  },
}

// Initialize React App
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <App />
            <Toaster
              toastOptions={toasterOptions}
              containerClassName="z-50"
            />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>,
)

// Performance Monitoring - React Query DevTools (development only)
if (import.meta.env.DEV) {
  // Note: DevTools can be manually imported if needed
  // import('@tanstack/react-query-devtools').then(({ ReactQueryDevtools }) => {
  //   console.log('React Query DevTools available')
  // })
  console.log('Development mode: React Query DevTools can be added manually if needed')
}

// Global Error Handler
window.addEventListener('error', (error) => {
  console.error('Global error:', error)
  // Send to error tracking service in production
  if (import.meta.env.PROD) {
    // TODO: Send to error tracking service
  }
})

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason)
  // Send to error tracking service in production
  if (import.meta.env.PROD) {
    // TODO: Send to error tracking service
  }
})